"use client";

import React, { useEffect, useState } from "react";

interface Parts {
  d: number;
  h: number;
  m: number;
  s: number;
  done: boolean;
}

function calc(targetMs: number): Parts {
  const diff = targetMs - Date.now();
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true };
  const sec = Math.floor(diff / 1000);
  return {
    d: Math.floor(sec / 86400),
    h: Math.floor((sec % 86400) / 3600),
    m: Math.floor((sec % 3600) / 60),
    s: sec % 60,
    done: false,
  };
}

/**
 * CountdownTimer — live ticking countdown to `target` (ISO date), shown
 * as Days / Hrs / Min / Sec tiles styled for a dark gradient hero.
 *
 * Client-only timing: renders placeholders on the server and fills in
 * after mount to avoid hydration mismatch (server vs client clock).
 */
export function CountdownTimer({
  target,
  labels,
  variant = "dark",
}: {
  target: string;
  labels: { d: string; h: string; m: string; s: string; done: string };
  /** "dark" = white-on-gradient (hero); "light" = dark-on-light bg. */
  variant?: "dark" | "light";
}) {
  const targetMs = new Date(target).getTime();
  const [p, setP] = useState<Parts | null>(null);

  useEffect(() => {
    if (Number.isNaN(targetMs)) return;
    setP(calc(targetMs));
    const id = setInterval(() => setP(calc(targetMs)), 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  if (Number.isNaN(targetMs)) return null;

  const light = variant === "light";
  const tileClass = light
    ? "flex min-w-[58px] flex-col items-center rounded-xl px-3 py-2 ring-1 sm:min-w-[68px]"
    : "flex min-w-[58px] flex-col items-center rounded-xl bg-white/10 px-3 py-2 ring-1 ring-white/20 backdrop-blur-sm sm:min-w-[68px]";
  const tileStyle: React.CSSProperties = light
    ? {
        backgroundColor: "color-mix(in srgb, var(--color-primary) 6%, transparent)",
        // ring colour via boxShadow so we can use a theme token
        boxShadow: "inset 0 0 0 1px var(--color-border)",
      }
    : {};
  const numColor = light ? "var(--color-primary)" : "#FFFFFF";
  const labelClass = light
    ? "mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
    : "mt-1 text-[10px] uppercase tracking-[0.18em] text-white/70";

  if (p?.done) {
    return (
      <div
        className={
          light
            ? "inline-flex items-center rounded-xl px-5 py-3 text-base font-bold tracking-wide ring-1"
            : "inline-flex items-center rounded-xl bg-white/15 px-5 py-3 text-base font-bold tracking-wide text-white ring-1 ring-white/25 backdrop-blur-sm"
        }
        style={
          light
            ? { color: "var(--color-primary)", boxShadow: "inset 0 0 0 1px var(--color-border)" }
            : {}
        }
        suppressHydrationWarning
      >
        {labels.done}
      </div>
    );
  }

  const items: Array<[number | undefined, string]> = [
    [p?.d, labels.d],
    [p?.h, labels.h],
    [p?.m, labels.m],
    [p?.s, labels.s],
  ];

  return (
    <div className="inline-flex gap-2.5 sm:gap-3" suppressHydrationWarning>
      {items.map(([v, lab], i) => (
        <div key={i} className={tileClass} style={tileStyle}>
          <span
            className="text-2xl font-bold leading-none sm:text-3xl"
            style={{ fontFamily: "var(--font-serif)", color: numColor }}
          >
            {p ? (i === 0 ? String(v) : String(v).padStart(2, "0")) : "--"}
          </span>
          <span className={labelClass}>{lab}</span>
        </div>
      ))}
    </div>
  );
}

export default CountdownTimer;
